<template>
    <b-container fluid class="flex-grow-1 d-flex flex-column px-0">
        <div class="p-3" v-show="this.$route.name === 'jobs'">
            <section class="grid-filters" >
                <b-row align-h="between" class="mb-3">
                    <b-col
                            cols="12"
                            sm="5"
                            md="4"
                            class="order-0 d-sm-block mb-0"
                            :class="{'d-none': !showSearch}"
                    >
                        <search-filter
                                :showSearch="showSearch"
                                v-model="query.search"
                        >
                        </search-filter>
                    </b-col>
                </b-row>
            </section>

            <b-row class="flex-grow-1">
                <b-col class="h-100 d-flex flex-column">
                    <b-row class="grid-count mb-3 justify-content-between align-items-baseline">
                        <b-col cols="8" class="d-flex align-items-baseline text-dark">
                            <span>
                                {{ count }} Results
                                <span class="font-weight-bold"
                                      v-if="selectedCount > 0"
                                >
                                    | {{ selectedCount }} selected
                                </span>
                            </span>
                        </b-col>
                        <b-col cols="4" class="d-flex justify-content-end">
                            <reset-filters
                                    @click="resetGridToDefaultOptions"></reset-filters>
                            <refresh-button
                                    :spinning="refreshing"
                                    @click="refresh"
                            ></refresh-button>
                        </b-col>
                    </b-row>
                    <div class="grid-wrapper flex-grow-1">
                        <ag-grid-vue
                                :style="'width: ' + gridWidth +'%; height: 76vh;'"
                                class="ag-theme-balham"

                                    :gridOptions="gridOptions"
                                    :defaultColDef="defaultColDef"
                                    :columnDefs="columnDefs"
                                    :rowData="rowData"
                                    :columnTypes="columnTypes"
                                    :rowSelection="rowSelection"

                                :side-bar="sideBarDef"
                                @gridReady="onGridReady"
                                :floatingFilter="floatingFilter"
                                :quickFilter="query.search"
                                :cacheQuickFilter="cacheQuickFilter"

                                :getContextMenuItems="getContextMenuItems"
                                :suppressExcelExport="suppressExcelExport"

                                :rowSelected="updateRowSelected"

                                :headerHeight="rowHeight"
                                :rowHeight="rowHeight"
                                @cellClicked="onCellClicked"

                                @columnMoved="saveGridOptions"
                                @columnPinned="saveGridOptions"
                                @columnResized="saveGridOptions"
                                @columnVisible="saveGridOptions"
                                @filterChanged="saveGridOptions"
                                @sortChanged="saveGridOptions"
                        >
                        </ag-grid-vue>
                    </div>
                </b-col>
            </b-row>
        </div>
        <router-view name="job"></router-view>
    </b-container>
</template>


<script>
import { AgGridVue } from 'ag-grid-vue';
import { mapGetters } from 'vuex';
import {
  jobGridColumnTypes,
  jobDefaultColDef as gridDefaultColDef,
  jobGridColumns as gridColumns,
} from '@/services/Job/JobListingGrid';
import GridMixin from '@/components/Grid/GridMixin';
import Job from '@/services/Job/Job';
import ResetFilters from '@/components/Grid/ResetFilters';
import RefreshButton from '@/components/Grid/RefreshButton';
import SearchFilter from '@/components/Grid/SearchFilter';
import debounce from 'lodash/debounce';

export default {
  name: 'Jobs',
  components: {
    AgGridVue,
    ResetFilters,
    RefreshButton,
    SearchFilter,
  },
  mixins: [GridMixin],
  data() {
    return {
      modelId: 'jobId',
      query: {
        search: '',
      },
      defaultColDef: gridDefaultColDef,
      columnDefs: gridColumns,
      columnTypes: jobGridColumnTypes,
    };
  },

  computed: {
    ...mapGetters({
      showSearch: 'propertyShowSearch',
    }),
  },

  watch: {
    '$store.getters.propertyTenure': function (value) {
      this.refresh();
    },
  },

  created() {
  },

  mounted() {
  },

  methods: {
    postGridReady(params) {
      // Keeping this as a commented out method as they will soon want this
      // re-enabled but with different some tweaked logic
      // this.setRowClasses();

      this.refresh();
      this.gridApi.sizeColumnsToFit();
    },

    /**
     * Async
     * Refreshes the table
     * Sets the grid options afterwards
     */
    async refresh() {
      this.refreshing = true;
      // Wait until the data is set, external dropdown filters are set
      await this.refreshData();
      // Then we set the grid options
      // We set a timeout to disallow any future grid saving that happens
      // As filters are applied immediately after;
      setTimeout(() => {
        this.refreshing = false;
      }, 1);

      // Then we set the grid options
      this.setGridOptions();

      this.postRefresh();
    },

    /**
     * Call after every refresh
     * */
    postRefresh() {
      this.updateRowSelected();
    },

    /**
     * Sets the rowdata with data from the server
     */
    refreshData() {
      this.refreshing = true;

      let rowData = [];
      const params = {
        complianceTypeId: this.$store.getters.complianceTypeId,
        completed: 0,
        draft: 0,
        propertyTenure: this.$store.getters.propertyTenure,
      };
      return Job.list(params)
        .then((response) => {
          rowData = response.data.data;
        })
        .catch((error) => {
          this.$notify({
            duration: 5000,
            title: 'Error',
            text: 'We couldn\'t obtain the job right now. Please try again later.',
            type: 'error',
          });
        })
        .finally(() => {
          this.rowData = rowData;
        });
    },

    onCellClicked(node) {
      const { jobId } = node.data;
      this.$router.push({ name: 'job', params: { jobId } });
    },

    /**
     * Sets the grid up with:
     * Column state
     * Sort
     * Filters
     */
    setGridOptions() {
      const name = 'jobs';

      let gridState = this.$store.getters.gridState(name);
      let gridSort = this.$store.getters.gridSort(name);
      let gridFilter = this.$store.getters.gridFilter(name);

      if (!gridState) {
        gridState = this.gridDefaults.state;
      }

      if (!gridSort) {
        gridSort = this.gridDefaults.sort;
      }

      if (!gridFilter) {
        gridFilter = this.gridDefaults.filter;
      }

      this.columnApi.setColumnState(gridState);

      this.gridApi.setSortModel(gridSort);
      this.gridApi.setFilterModel(gridFilter);
      this.gridApi.onFilterChanged();
    },

    /**
     * Only allow to save the grid if it is not refreshing data
     * */
    saveGridOptions() {
      if (this.refreshing) {
        return false;
      }

      return this.saveOptions();
    },

    /**
     * Save the grid options (only allowed once per second)
     */
    saveOptions: debounce(function () {
      const name = 'jobs';
      const state = this.columnApi.getColumnState();
      const sort = this.gridApi.getSortModel();
      const filter = this.gridApi.getFilterModel();

      this.$store.dispatch('saveGridOptions', {
        name,
        state,
        sort,
        filter,
      });
    }, 1000),
  },
};
</script>

<style lang="scss">

</style>
